/**
 * "What Flux Agent knows about you" dialog. Lists the signed-in user's memory documents, lets the
 * user correct one with version-checked saves, and delete one after confirmation. Only per-user
 * memory is shown here; global playbooks and the system prompt live in the trainer console.
 *
 * @module apps/web/src/components/flux-agent/FluxAgentMemoryDialog
 */

import { useCallback, useEffect, useState } from 'react';
import { Brain, ChevronLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FluxAgentDocumentEditor } from './FluxAgentDocumentEditor';
import { FluxAgentLearningPanel } from './learning/FluxAgentLearningPanel';
import { FluxAgentBestTimes } from './timing/FluxAgentBestTimes';
import { fluxAgentApi, type FluxAgentDocument } from '@/lib/fluxAgentApi';
import { cn } from '@/lib/utils';

/** Inputs for {@link FluxAgentMemoryDialog}. */
export interface FluxAgentMemoryDialogProps {
  /** Whether the dialog is shown. */
  open: boolean;
  /** Called with false when the user closes the dialog. */
  onOpenChange: (open: boolean) => void;
}

/** Browse, correct, and delete the agent's memory documents about the current user. */
export function FluxAgentMemoryDialog({ open, onOpenChange }: FluxAgentMemoryDialogProps) {
  const [documents, setDocuments] = useState<FluxAgentDocument[]>([]);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'notes' | 'learning' | 'times'>('notes');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await fluxAgentApi.listMemory();
    setLoading(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    const sorted = [...result.data.documents].sort((a, b) => a.path.localeCompare(b.path));
    setDocuments(sorted);
    setSelectedPath(current =>
      current && sorted.some(doc => doc.path === current) ? current : null
    );
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const selected = documents.find(doc => doc.path === selectedPath) || null;

  const remove = async (document: FluxAgentDocument) => {
    if (!window.confirm(`Delete “${document.path}”? Flux Agent will forget what it noted there.`))
      return;
    const result = await fluxAgentApi.deleteMemory(document.path);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setSelectedPath(null);
    await load();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(92dvh,48rem)] max-w-4xl flex-col">
        <DialogHeader className="pr-8">
          <DialogTitle className="flex items-center gap-2">
            <Brain className="text-primary h-5 w-5" /> What Flux Agent knows about you
          </DialogTitle>
          <DialogDescription>
            Flux Agent keeps these notes to coach you better. Correct anything that’s wrong, or
            delete it.
          </DialogDescription>
        </DialogHeader>

        <div role="tablist" aria-label="What Flux Agent knows" className="flex flex-wrap gap-1">
          {(
            [
              ['notes', 'Notes'],
              ['learning', 'Learned from conversations'],
              ['times', 'Best times'],
            ] as const
          ).map(([value, label]) => (
            <Button
              key={value}
              type="button"
              role="tab"
              size="sm"
              variant={tab === value ? 'default' : 'ghost'}
              aria-selected={tab === value}
              onClick={() => setTab(value)}
            >
              {label}
            </Button>
          ))}
        </div>
        {/* Both panels mount with the dialog and load in the background, so switching tabs
            shows their content at once instead of a spinner and a server round trip. */}
        <div
          className={cn('min-h-0 flex-1 overflow-y-auto', tab !== 'learning' && 'hidden')}
          role="tabpanel"
          hidden={tab !== 'learning'}
        >
          <FluxAgentLearningPanel />
        </div>
        <div
          className={cn('min-h-0 flex-1 overflow-y-auto', tab !== 'times' && 'hidden')}
          role="tabpanel"
          hidden={tab !== 'times'}
        >
          <FluxAgentBestTimes />
        </div>

        {error && (
          <p
            role="alert"
            className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-xs"
          >
            {error}
          </p>
        )}

        <div
          className={cn(
            'grid min-h-0 flex-1 gap-4 md:grid-cols-[14rem_1fr]',
            tab !== 'notes' && 'hidden'
          )}
        >
          <div className={cn('min-h-0 overflow-y-auto', selected && 'hidden md:block')}>
            {loading && !documents.length ? (
              <p className="text-muted-foreground flex items-center gap-2 text-xs">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </p>
            ) : documents.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Nothing yet. As you talk, Flux Agent will note things like when your energy dips and
                what drains you.
              </p>
            ) : (
              <ul className="space-y-1" aria-label="Memory documents">
                {documents.map(doc => (
                  <li key={doc.path}>
                    <button
                      type="button"
                      onClick={() => setSelectedPath(doc.path)}
                      aria-current={doc.path === selectedPath ? 'true' : undefined}
                      className={cn(
                        'focus-visible:ring-ring w-full truncate rounded-md px-3 py-2 text-left font-mono text-xs transition-colors focus-visible:outline-none focus-visible:ring-2',
                        doc.path === selectedPath
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-accent'
                      )}
                    >
                      {doc.path}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={cn('min-h-0 overflow-y-auto', !selected && 'hidden md:block')}>
            {selected ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mb-2 gap-1 md:hidden"
                  onClick={() => setSelectedPath(null)}
                >
                  <ChevronLeft className="h-4 w-4" /> All notes
                </Button>
                <FluxAgentDocumentEditor
                  document={selected}
                  label="Note contents (Markdown)"
                  onSave={content =>
                    fluxAgentApi.saveMemory(
                      selected.path,
                      content,
                      selected.version > 0 ? selected.version : undefined
                    )
                  }
                  onSaved={saved =>
                    setDocuments(current =>
                      current.map(doc => (doc.path === saved.path ? saved : doc))
                    )
                  }
                  onReload={load}
                  onDelete={() => void remove(selected)}
                />
              </>
            ) : (
              documents.length > 0 && (
                <p className="text-muted-foreground text-sm">Choose a note to read or edit it.</p>
              )
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
